import { Stack } from 'expo-router';
import React, { FC } from 'react';
import { Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { StoreLocatorV2View } from '@fhs/store-locator';
import { Box } from '@fhs-legacy/universal-components';
import { useAreFlagsReady, useFlag } from 'utils/launchdarkly';
import { STORE_LOCATOR_CONTAINER } from 'utils/test-ids';

import { ItemsUnavailableDialog } from './items-unavailable-dialog';
import { StoreLocatorProvider, useStoreLocatorContext } from './store-locator-provider';
import { StoreLocatorScreenHeader } from './store-locator-screen-header';
import { StoreLocatorView } from './store-locator.view';

/**
 *
 * StoreLocator purpose
 *
 */
const StoreLocator: FC<React.PropsWithChildren<unknown>> = () => {
  const context = useStoreLocatorContext();
  const hasStoreLocatorProvider = Object.keys(context).length > 0;
  const enableStoreLocatorV2 = useFlag('enable-store-locator-v2');
  const areFlagsReady = useAreFlagsReady();
  // Screen options persist across this swap, so both shells have to set a complete
  // header config here. Keep the header and the body off until LaunchDarkly has
  // settled: the flag falls back to off, and the legacy shell from that fallback
  // flashes on Android before v2 replaces it. v2 owns its own panel chrome; the
  // legacy view still uses the navigation header (and its status-bar inset).
  const showLegacyShell = areFlagsReady && !enableStoreLocatorV2;
  const content = (
    <>
      <Stack.Screen
        options={
          showLegacyShell
            ? {
                headerShown: true,
                header: ({ navigation }) => <StoreLocatorScreenHeader navigation={navigation} />,
              }
            : { headerShown: false }
        }
      />
      {areFlagsReady &&
        (enableStoreLocatorV2 ? (
          <>
            <StoreLocatorV2View />
            <ItemsUnavailableDialog />
          </>
        ) : (
          <SafeAreaView edges={Platform.OS === 'android' ? ['bottom'] : []} style={{ flex: 1 }}>
            <Box flex={1} testID={STORE_LOCATOR_CONTAINER}>
              <StoreLocatorView />
              <ItemsUnavailableDialog />
            </Box>
          </SafeAreaView>
        ))}
    </>
  );

  return hasStoreLocatorProvider ? content : <StoreLocatorProvider>{content}</StoreLocatorProvider>;
};

export default StoreLocator;
